import { useState } from "react";
import { Link } from "react-router-dom";
import { Lock, Loader2, Gift, Repeat } from "lucide-react";
import { useMeta } from "@/lib/useMeta";
import { toast } from "sonner";
import { api, gbp, errMsg, imgSrc, toApiItems } from "@/lib/api";
import { useQuote } from "@/lib/useQuote";
import { useCart } from "@/context/CartContext";

const FIELDS = [
  ["name", "Full name", "col-span-2"],
  ["email", "Email (for order updates)", "col-span-2"],
  ["phone", "Phone (optional, for Evri)", "col-span-2"],
  ["line1", "Address line 1", "col-span-2"],
  ["line2", "Address line 2 (optional)", "col-span-2"],
  ["city", "Town / City", "col-span-1"],
  ["postcode", "Postcode", "col-span-1"],
];
const POSTCODE_RE = /^[A-Z]{1,2}\d[A-Z\d]?\s?\d[A-Z]{2}$/i;

export default function Checkout() {
  const { items, subtotal } = useCart();
  const [form, setForm] = useState({ name: "", email: "", phone: "", line1: "", line2: "", city: "", postcode: "" });
  const [opt, setOpt] = useState("evri_standard");
  const [busy, setBusy] = useState(false);
  const [gift, setGift] = useState(false);
  const [giftMsg, setGiftMsg] = useState("");
  const quote = useQuote(items);
  const option = quote?.options?.find((o) => o.id === opt);
  const fee = option ? option.fee : 0;
  const meta = useMeta();
  const [wrap, setWrap] = useState(false);
  const wrapPrice = meta?.gift_wrap_price || 0;
  const wrapFee = wrap && wrapPrice > 0 ? wrapPrice : 0;
  const [sub, setSub] = useState(false);
  const [code, setCode] = useState("");
  const [applied, setApplied] = useState(null);
  const [checking, setChecking] = useState(false);
  const discount = sub || applied ? items.reduce((s, i) => s + (i.price - Math.round(i.price * 90) / 100) * i.qty, 0) : 0;
  const applyCode = async () => {
    if (!form.email) return toast.error("Enter your email above first");
    setChecking(true);
    try { const { data } = await api.post("/discount/check", { code, email: form.email }); setApplied(data.code); toast.success(`${data.code} applied — ${data.percent}% off`); }
    catch (e2) { setApplied(null); toast.error(errMsg(e2)); }
    setChecking(false);
  };
  const [gcCode, setGcCode] = useState("");
  const [gc, setGc] = useState(null);
  const before = subtotal - discount + fee + wrapFee;
  const gcUsed = gc ? Math.min(gc.balance, before) : 0;
  const total = Math.max(0, before - gcUsed);
  const applyGc = async () => {
    try { const { data } = await api.post("/giftcards/check", { code: gcCode }); setGc(data); toast.success(`Gift card applied — ${gbp(data.balance)} available`); }
    catch (e2) { setGc(null); toast.error(errMsg(e2)); }
  };

  if (items.length === 0)
    return <div className="max-w-3xl mx-auto px-4 py-24 text-center" data-testid="checkout-empty"><p className="font-serif text-3xl">Your basket is empty</p><Link to="/" className="mt-6 inline-block underline">Continue shopping</Link></div>;

  const submit = async (e) => {
    e.preventDefault();
    if (!POSTCODE_RE.test(form.postcode.trim())) return toast.error("Please enter a valid UK postcode");
    setBusy(true);
    try {
      const { data } = await api.post("/checkout", {
        items: toApiItems(items),
        customer: { ...form, postcode: form.postcode.trim().toUpperCase() },
        delivery_option: opt,
        gift_message: gift ? giftMsg : "",
        gift_wrap: wrapFee > 0,
        subscribe: sub,
        discount_code: applied || "",
        gift_card_code: gc ? gc.code : "",
        origin_url: window.location.origin,
      });
      window.location.href = data.checkout_url;
    } catch (e2) {
      toast.error(errMsg(e2));
      setBusy(false);
    }
  };

  return (
    <form onSubmit={submit} className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 grid lg:grid-cols-12 gap-10" data-testid="checkout-form">
      <div className="lg:col-span-7 space-y-10">
        <div>
          <h1 className="font-serif text-4xl font-bold">Checkout</h1>
          <p className="mt-2 text-slate-600 text-sm">No account needed — we'll email your order updates.</p>
        </div>
        <section>
          <h2 className="font-semibold text-lg">Delivery address (UK only)</h2>
          <div className="mt-4 grid grid-cols-2 gap-4">
            {FIELDS.map(([k, label, span]) => (
              <label key={k} className={`${span} text-sm`}>
                <span className="text-slate-600">{label}</span>
                <input
                  required={!["phone", "line2"].includes(k)}
                  type={k === "email" ? "email" : "text"}
                  value={form[k]}
                  onChange={(e) => { setForm({ ...form, [k]: e.target.value }); if (k === "email") setApplied(null); }}
                  className="mt-1.5 w-full rounded-xl border border-[#E2D7C3] bg-white px-4 py-3 outline-none focus:ring-2 focus:ring-[#8C2220]/30"
                  data-testid={`checkout-${k}-input`}
                />
              </label>
            ))}
          </div>
        </section>
        <section>
          <h2 className="font-semibold text-lg">Evri delivery</h2>
          {quote?.options && <p className="mt-1 text-sm text-slate-500" data-testid="parcel-weight">Estimated parcel weight {quote.weight_kg}kg{option?.parcels > 1 && ` · sent as ${option.parcels} Evri parcels`}</p>}
          <div className="mt-4 grid sm:grid-cols-2 gap-4">
            {!quote && <p className="text-sm text-slate-500">Working out postage…</p>}
            {quote?.error && <p className="col-span-2 rounded-xl bg-rose-50 text-rose-800 text-sm p-3" data-testid="checkout-stock-error">{quote.error} — please reduce the quantity in your basket.</p>}
            {quote?.options?.map((o) => (
              <button type="button" key={o.id} onClick={() => setOpt(o.id)} className={`text-left rounded-2xl border-2 p-5 transition-colors ${opt === o.id ? "border-[#8C2220] bg-[#8C2220]/5" : "border-[#E2D7C3] bg-white hover:border-slate-400"}`} data-testid={`delivery-option-${o.id}`}>
                <p className="font-semibold">{o.label}</p>
                <p className="text-sm text-slate-500">{o.time}</p>
                <p className="mt-3 font-serif text-xl font-bold" data-testid={`delivery-fee-${o.id}`}>{o.fee === 0 ? "FREE" : gbp(o.fee)}</p>
                {o.id === "evri_standard" && quote.free_over != null && <p className="text-xs text-slate-500">Free over {gbp(quote.free_over)}</p>}
              </button>
            ))}
          </div>
        </section>
        <section className="rounded-2xl bg-[#F7F3EB] p-5" data-testid="gift-section">
          <label className="flex items-center gap-3 font-semibold cursor-pointer">
            <input type="checkbox" checked={gift} onChange={(e) => setGift(e.target.checked)} className="w-4 h-4 accent-[#8C2220]" data-testid="gift-toggle" />
            <Gift className="w-5 h-5 text-[#8C2220]" /> This is a gift — add a personal message
          </label>
          {gift && (
            <div className="mt-4">
              <textarea maxLength={300} rows={3} value={giftMsg} onChange={(e) => setGiftMsg(e.target.value)} placeholder="Happy birthday! With love from…" className="w-full rounded-xl border border-[#E2D7C3] bg-white px-4 py-3 font-serif italic outline-none focus:ring-2 focus:ring-[#8C2220]/30" data-testid="gift-message-input" />
              <p className="text-xs text-slate-500 mt-1">{giftMsg.length}/300 · We'll write this on a card in the parcel. Prices aren't shown on the card.</p>
            </div>
          )}
          {wrapPrice > 0 && (
            <label className="mt-4 flex items-center gap-3 font-semibold cursor-pointer" data-testid="gift-wrap-option">
              <input type="checkbox" checked={wrap} onChange={(e) => setWrap(e.target.checked)} className="w-4 h-4 accent-[#8C2220]" data-testid="gift-wrap-toggle" />
              <img src="/gift-wrap.jpg" alt="Navy gift wrapping with cream ribbon" className="w-16 h-16 rounded-xl object-cover border border-[#E2D7C3]" data-testid="gift-wrap-photo" />
              <span>Add gift wrapping <span className="font-normal text-slate-600">+{gbp(wrapPrice)}</span>
                <small className="block font-normal text-xs text-slate-500">Navy paper, cream satin ribbon and a gift tag</small></span>
            </label>
          )}
        </section>
        <section className="rounded-2xl border-2 border-dashed border-[#1E2A44]/30 p-5" data-testid="subscribe-section">
          <label className="flex items-start gap-3 cursor-pointer">
            <input type="checkbox" checked={sub} onChange={(e) => { setSub(e.target.checked); if (e.target.checked) { setApplied(null); setCode(""); setGc(null); setGcCode(""); } }} className="mt-1 w-4 h-4 accent-[#1E2A44]" data-testid="subscribe-toggle" />
            <span><span className="font-semibold flex items-center gap-2"><Repeat className="w-4 h-4 text-[#1E2A44]" /> Subscribe &amp; save 10% — get this box every month</span>
              <small className="block text-sm text-slate-600 mt-1">We'll post the same items every month and charge your card automatically. Cancel any time from the order tracking page.</small></span>
          </label>
        </section>
      </div>
      <aside className="lg:col-span-5">
        <div className="lg:sticky lg:top-32 rounded-3xl bg-white border border-[#EDE4D3] p-6">
          <h2 className="font-serif text-2xl font-semibold">Order summary</h2>
          <div className="mt-4 divide-y divide-[#EDE4D3]">
            {items.map((i) => (
              <div key={i.id} className="py-3 flex items-center gap-3 text-sm">
                <img src={imgSrc(i.image_url)} alt="" className="w-12 h-12 object-contain rounded-lg bg-[#F7F3EB] p-1" />
                <span className="flex-1">{i.name} × {i.qty}{i.contents && <small className="block text-xs text-slate-500" data-testid="checkout-hamper-contents">{i.contents.join(" · ")}</small>}</span>
                <span className="font-medium">{gbp(i.price * i.qty)}</span>
              </div>
            ))}
          </div>
          <div className="mt-4 space-y-2 text-sm border-t border-[#EDE4D3] pt-4">
            <div className="flex justify-between"><span>Subtotal</span><span data-testid="summary-subtotal">{gbp(subtotal)}</span></div>
            {discount > 0 && <div className="flex justify-between text-emerald-700"><span>{applied ? `Welcome discount (${applied})` : <>Subscribe &amp; save 10%</>}</span><span data-testid="summary-subscribe-discount">−{gbp(discount)}</span></div>}
            <div className="flex justify-between"><span>{option?.label || "Delivery"}</span><span data-testid="summary-delivery">{fee === 0 ? "FREE" : gbp(fee)}</span></div>
            {wrapFee > 0 && <div className="flex justify-between"><span>Gift wrapping</span><span data-testid="summary-gift-wrap">{gbp(wrapFee)}</span></div>}
            {gcUsed > 0 && <div className="flex justify-between text-emerald-700"><span>Gift card ({gc.code.slice(-4)})</span><span data-testid="summary-gift-card">−{gbp(gcUsed)}</span></div>}
            <div className="flex justify-between text-lg font-bold pt-2"><span>Total</span><span data-testid="summary-total">{gbp(total)}</span></div>
          </div>
          {!sub && (
            <div className="mt-4 flex gap-2" data-testid="discount-section">
              <input value={code} onChange={(e) => { setCode(e.target.value.toUpperCase()); setApplied(null); }} placeholder="Discount code" className="flex-1 rounded-full border border-[#E2D7C3] px-4 py-2 text-sm font-mono uppercase outline-none focus:ring-2 focus:ring-[#8C2220]/30" data-testid="discount-code-input" />
              <button type="button" disabled={!code.trim() || checking || !!applied} onClick={applyCode} className="rounded-full border border-[#1B3626] text-[#1B3626] px-4 py-2 text-sm font-semibold disabled:opacity-40" data-testid="discount-apply-button">{applied ? "Applied" : checking ? "Checking…" : "Apply"}</button>
            </div>
          )}
          {!sub && (
            <div className="mt-2 flex gap-2" data-testid="giftcard-section">
              <input value={gcCode} onChange={(e) => { setGcCode(e.target.value.toUpperCase()); setGc(null); }} placeholder="Gift card number" className="flex-1 rounded-full border border-[#E2D7C3] px-4 py-2 text-sm font-mono uppercase outline-none focus:ring-2 focus:ring-[#8C2220]/30" data-testid="giftcard-code-input" />
              <button type="button" disabled={gcCode.trim().length < 4 || !!gc} onClick={applyGc} className="rounded-full border border-[#1B3626] text-[#1B3626] px-4 py-2 text-sm font-semibold disabled:opacity-40" data-testid="giftcard-apply-button">{gc ? "Applied" : "Apply"}</button>
            </div>
          )}
          {gc && gc.balance > gcUsed && <p className="mt-1 text-xs text-slate-500" data-testid="giftcard-remaining">{gbp(gc.balance - gcUsed)} will stay on your gift card for next time.</p>}
          <p className="mt-4 text-xs text-slate-500 bg-[#F7F3EB] rounded-xl p-3">We shop for your items after you pay. Anything out of stock is refunded to your card; if nothing is available we cancel and refund in full.</p>
          <button disabled={busy || !option} className="mt-5 w-full rounded-full bg-[#8C2220] text-white py-4 font-semibold flex items-center justify-center gap-2 hover:bg-[#701A18] disabled:opacity-60 transition-colors" data-testid="pay-now-button">
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Lock className="w-4 h-4" />} {sub ? `Subscribe ${gbp(total)}/month` : total === 0 && gc ? "Place order with gift card" : `Pay securely ${gbp(total)}`}
          </button>
        </div>
      </aside>
    </form>
  );
}
